import { useRestApiProvider } from "@jugl-web/rest-api";
import { useTranslations } from "@jugl-web/utils";
import { useCallback, useMemo } from "react";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { extractNonEmptyAccount } from "../../../utils/extractNonEmptyAccount";
import { useManageAccountsDrawer } from "../providers/ManageAccountsDrawerContext";

export const useVoiceAccount = () => {
  const { entityId } = useEntitySelectedProvider();
  const { navigateToPage } = useNavigation();
  const { crmConfig, openManageAccountDrawer, isInboxModule } =
    useManageAccountsDrawer();
  const { integrationsApi } = useRestApiProvider();
  const { t } = useTranslations();

  const [createVoiceAccount, { isLoading: isCreating }] =
    integrationsApi.useCreateVoiceAccountMutation({
      fixedCacheKey: "voice-create",
    });
  const [disconnectVoiceAccount, { isLoading: isDisconnecting }] =
    integrationsApi.useDisconnectVoiceAccountMutation({
      fixedCacheKey: "voice-disconnect",
    });

  const account = useMemo(
    () => extractNonEmptyAccount(crmConfig?.voice),
    [crmConfig?.voice]
  );

  const isConnected = useMemo(() => !!account?.id, [account]);

  const createAccount = useCallback(async () => {
    if (isConnected || isCreating) {
      return true;
    }

    try {
      await createVoiceAccount({
        entityId,
        meta: {
          successMessage: t({
            id: "client-support-page.voice-was-enabled",
            defaultMessage: "Phone calls was enabled",
          }),
        },
      }).unwrap();
      return true;
    } catch {
      // Error UI handled via meta + apiResultMiddleware
      return false;
    }
  }, [createVoiceAccount, entityId, isConnected, isCreating, t]);

  const disconnectAccount = useCallback(async () => {
    try {
      await disconnectVoiceAccount({
        entityId,
        meta: {
          successMessage: t({
            id: "client-support-page.voice-was-disconnected",
            defaultMessage: "Phone calls was disconnected",
          }),
        },
      }).unwrap();
      openManageAccountDrawer(undefined);
      if (isInboxModule) {
        navigateToPage("clientSupport");
      }
      return true;
    } catch {
      // Error UI handled via meta + apiResultMiddleware
      return false;
    }
  }, [
    disconnectVoiceAccount,
    entityId,
    isInboxModule,
    navigateToPage,
    openManageAccountDrawer,
    t,
  ]);

  return {
    account,
    isConnected,
    createAccount,
    disconnectAccount,
    isCreating,
    isDisconnecting,
  };
};
